import React, { useEffect, useState } from "react";
import {
  Button,
  ButtonGroup,
  IconButton,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { format } from "date-fns";
import { Archive, PencilSimpleLine } from "@phosphor-icons/react";
import * as selectors from "selectors";
import actions from "actions";
import SectionWrapper from "../../Widgets/SectionWrapper";
import ListItemSection from "../../Widgets/ListItemSection";
import InsurancePanelView from "./InsurancePanelView";
import InsurancePanelEditForm from "./InsurancePanelEditForm";
import { SecondaryAsPrimaryDialog } from "./SecondaryAsPrimaryDialog";
import { InsuranceAsInactiveDialog } from "./InsuranceAsInactiveDialog";
import { PrimaryInsuranceAsInactiveDialog } from "./PrimaryInsuranceAsInactiveDialog";
import { dateWithoutTimezone } from "utils/dateWithoutTimezone";
import SidePanelDrawer from "../../Widgets/SidePanelDrawer";

const INSURANCE_TABS = {
  currentCoverage: "currentCoverage",
  pastCoverage: "pastCoverage",
} as const;

type InsuranceTabType = (typeof INSURANCE_TABS)[keyof typeof INSURANCE_TABS];

/**
 * Insurance component that manages its own state and data fetching.
 * This component handles insurance coverage independently with its own edit mode.
 * Extracts clientId from URL parameters - no props required.
 */
const Insurance: React.FC = () => {
  const { id: clientId } = useParams<{ id: string }>();
  const dispatch = useDispatch();
  const userInsurance: any = useSelector(selectors.getUserInsurance);
  const isCaregiver = useSelector(selectors.isCaregiver);
  const userPermissions: any = useSelector(selectors.getUserPermissionsList);
  const canEdit = !isCaregiver && Boolean(userPermissions?.add_client_insurance);

  const [selectedTab, setSelectedTab] = useState<InsuranceTabType>(INSURANCE_TABS.currentCoverage);
  const [sidePanelContent, setSidePanelContent] = useState<React.ReactNode>(null);
  const [sidePanelTitle, setSidePanelTitle] = useState<string>("");
  const [showSecondaryAsPrimaryDialog, setShowSecondaryAsPrimaryDialog] = useState(false);
  const [pendingPrimaryInsuranceData, setPendingPrimaryInsuranceData] = useState<any>(null);
  const [showInsuranceAsInactiveDialog, setShowInsuranceAsInactiveDialog] = useState(false);
  const [showPrimaryInsuranceAsInactiveDialog, setShowPrimaryInsuranceAsInactiveDialog] =
    useState(false);
  const [insuranceToInactivate, setInsuranceToInactivate] = useState<any>(null);

  useEffect(() => {
    if (clientId) {
      dispatch(actions.getInsuranceProvidersList());
      dispatch(actions.getUserInsurance(parseInt(clientId)));
    }
  }, [clientId, dispatch]);

  const userInsuranceSorted = React.useMemo(() => {
    const currentCoverage: any[] = [];
    const pastCoverage: any[] = [];

    userInsurance?.forEach((insurance: any) => {
      const today = new Date();
      const expirationDate = new Date(insurance.expiration_date);

      if (!insurance.active) {
        pastCoverage.push(insurance);
      } else if (!insurance.expiration_date || expirationDate > today) {
        currentCoverage.push(insurance);
      } else {
        pastCoverage.push(insurance);
      }
    });

    return { currentCoverage, pastCoverage };
  }, [userInsurance]);

  const showBadge = (insurance: any) => {
    return (
      insurance?.is_primary &&
      insurance?.active &&
      (!insurance?.expiration_date || new Date(insurance?.expiration_date) > new Date())
    );
  };

  const primaryActiveExists = React.useMemo(() => {
    return userInsuranceSorted[INSURANCE_TABS.currentCoverage].some(
      (insurance: any) => insurance.is_primary === true
    );
  }, [userInsuranceSorted]);

  const handleSetInsuranceAsInactive = (insurance: any) => {
    if (insurance.is_primary && insurance.active) {
      const secondaryInsurance = userInsurance?.find((ins: any) => !ins.is_primary && ins.active);
      if (!secondaryInsurance) {
        setInsuranceToInactivate(insurance);
        setShowInsuranceAsInactiveDialog(true);
      } else {
        setInsuranceToInactivate(insurance);
        setShowPrimaryInsuranceAsInactiveDialog(true);
      }
    } else if (!insurance.is_primary && insurance.active) {
      setInsuranceToInactivate(insurance);
      setShowInsuranceAsInactiveDialog(true);
    }
  };

  const handleCloseSidePanel = (): void => {
    setSidePanelContent(null);
    setSidePanelTitle("");
  };

  const openInsurancePanel = (insurance: any, mode: "view" | "edit") => {
    setSidePanelTitle(
      mode === "edit"
        ? insurance
          ? "Edit Insurance"
          : "Add Insurance"
        : insurance?.insurance_name || "Insurance"
    );
    if (mode === "edit") {
      setSidePanelContent(
        <InsurancePanelEditForm
          insurance={insurance}
          userInsurance={userInsurance}
          primaryActiveExists={primaryActiveExists}
          clientId={clientId}
          setSidePanelContent={setSidePanelContent}
          setShowSecondaryAsPrimaryDialog={setShowSecondaryAsPrimaryDialog}
          setPendingPrimaryInsuranceData={setPendingPrimaryInsuranceData}
        />
      );
    } else {
      setSidePanelContent(<InsurancePanelView insurance={insurance} />);
    }
  };

  const renderContent = () => {
    if (!clientId) {
      return (
        <Typography variant="h6" color="error">
          Client ID is required
        </Typography>
      );
    }

    return (
      <Stack width="100%">
        <Stack gap={7}>
          <ToggleButtonGroup
            value={selectedTab}
            exclusive
            sx={{ width: "fit-content" }}
            onChange={(_, newValue) => {
              if (newValue !== null) setSelectedTab(newValue as InsuranceTabType);
            }}
          >
            <ToggleButton value={INSURANCE_TABS.currentCoverage}>Current Coverage</ToggleButton>
            <ToggleButton value={INSURANCE_TABS.pastCoverage}>Past Coverage</ToggleButton>
          </ToggleButtonGroup>

          <Stack gap="12px">
            {userInsuranceSorted[selectedTab].length === 0 ? (
              <Typography variant="body2" color="textSecondary">
                This client has no insurance information
              </Typography>
            ) : (
              userInsuranceSorted[selectedTab].map((insurance: any) => {
                const {
                  expiration_date,
                  subscriber_first_name,
                  subscriber_last_name,
                  inactive_date,
                } = insurance ?? {};

                const inactiveDate = expiration_date || inactive_date;
                const inactiveDateText = inactiveDate
                  ? `${format(new Date(dateWithoutTimezone(inactiveDate)), "MMM do yyyy")}`
                  : "No date";

                const subscriberName = `${subscriber_first_name || ""} ${subscriber_last_name || ""}`;
                const subtitleMap = {
                  [INSURANCE_TABS.currentCoverage]: `Subscriber: ${subscriberName} | Member ID: ${insurance?.member_id}`,
                  [INSURANCE_TABS.pastCoverage]: `${expiration_date ? "Expired" : "Set as inactive"} on ${inactiveDateText}`,
                };

                const isPastCoverage = selectedTab === INSURANCE_TABS.pastCoverage;

                return (
                  <ListItemSection
                    key={insurance.id}
                    title={insurance?.insurance_name}
                    subtitle={`${subtitleMap[selectedTab]}`}
                    isPrimary={showBadge(insurance)}
                    onClick={() => openInsurancePanel(insurance, "view")}
                    customEditComponent={
                      canEdit && !isPastCoverage ? (
                        <ButtonGroup variant="contained" size="small">
                          <Tooltip title="Edit coverage" placement="top">
                            <IconButton
                              onClick={() => openInsurancePanel(insurance, "edit")}
                              size="small"
                            >
                              <PencilSimpleLine weight="duotone" size={16} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Set as inactive" placement="top">
                            <IconButton
                              size="small"
                              onClick={() => handleSetInsuranceAsInactive(insurance)}
                            >
                              <Archive weight="duotone" size={16} />
                            </IconButton>
                          </Tooltip>
                        </ButtonGroup>
                      ) : undefined
                    }
                  />
                );
              })
            )}
          </Stack>

          {canEdit &&
            selectedTab !== INSURANCE_TABS.pastCoverage &&
            !(userInsuranceSorted.currentCoverage.length >= 2) && (
              <Button
                color="secondary"
                sx={{ width: "fit-content" }}
                onClick={() => {
                  setSidePanelContent(
                    <InsurancePanelEditForm
                      clientId={clientId}
                      primaryActiveExists={primaryActiveExists}
                      setSidePanelContent={setSidePanelContent}
                      setShowSecondaryAsPrimaryDialog={setShowSecondaryAsPrimaryDialog}
                      setPendingPrimaryInsuranceData={setPendingPrimaryInsuranceData}
                    />
                  );
                  setSidePanelTitle("Add Insurance");
                }}
              >
                Add Current Coverage
              </Button>
            )}
        </Stack>
      </Stack>
    );
  };

  return (
    <>
      <SectionWrapper
        title="Insurance"
        description="Add and manage insurance coverage on the client account."
        hideButtons={true}
      >
        {renderContent()}
      </SectionWrapper>

      <SidePanelDrawer
        open={Boolean(sidePanelContent)}
        onClose={handleCloseSidePanel}
        width={400}
        title={sidePanelTitle}
        bodyPadding="24px"
      >
        {sidePanelContent}
      </SidePanelDrawer>

      {showSecondaryAsPrimaryDialog && (
        <SecondaryAsPrimaryDialog
          open={showSecondaryAsPrimaryDialog}
          onClose={() => setShowSecondaryAsPrimaryDialog(false)}
          pendingData={pendingPrimaryInsuranceData}
        />
      )}

      <InsuranceAsInactiveDialog
        open={showInsuranceAsInactiveDialog}
        onClose={() => {
          setShowInsuranceAsInactiveDialog(false);
          setInsuranceToInactivate(null);
        }}
        insurance={insuranceToInactivate}
        clientId={clientId}
      />

      <PrimaryInsuranceAsInactiveDialog
        open={showPrimaryInsuranceAsInactiveDialog}
        onClose={() => {
          setShowPrimaryInsuranceAsInactiveDialog(false);
          setInsuranceToInactivate(null);
        }}
        insurance={insuranceToInactivate}
        clientId={clientId}
      />
    </>
  );
};

export default Insurance;
